/**
 * Region index — lights the preview image belonging to the row under the
 * pointer or the keyboard focus.
 *
 * Deliberately self-contained: no imports, so the section can load it with a
 * plain module tag and the theme's importmap stays untouched. Registering a
 * custom element is also what makes this survive the theme editor, which
 * re-renders a section on every settings change — `connectedCallback` runs
 * again on the new element, where an inline script would not.
 *
 * The index works without this file: every row is a real link.
 */
class NnaddRegionIndex extends HTMLElement {
  connectedCallback() {
    this.rows = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-index-row]')]);
    this.previews = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-index-preview]')]);
    if (!this.rows.length || !this.previews.length) return;

    // pointerenter and focus do not bubble, so each row carries its own listener.
    for (const row of this.rows) {
      row.addEventListener('pointerenter', this.show);
      row.addEventListener('focus', this.show);
    }
    this.addEventListener('pointerleave', this.clear);
    this.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    for (const row of this.rows ?? []) {
      row.removeEventListener('pointerenter', this.show);
      row.removeEventListener('focus', this.show);
    }
    this.removeEventListener('pointerleave', this.clear);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  show = (/** @type {Event} */ event) => {
    const id = /** @type {HTMLElement} */ (event.currentTarget).dataset.indexRow;
    for (const preview of this.previews) {
      preview.classList.toggle('is-shown', preview.dataset.indexPreview === id);
    }
  };

  clear = () => {
    for (const preview of this.previews) preview.classList.remove('is-shown');
  };

  /** Only clear when focus actually left the index, not when it moved between rows. */
  onFocusOut = (/** @type {FocusEvent} */ event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (!next || !this.contains(next)) this.clear();
  };
}

if (!customElements.get('nnadd-region-index')) {
  customElements.define('nnadd-region-index', NnaddRegionIndex);
}
